import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import QrCard from "../components/QrCard";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { toast } from "sonner";
import { NbButton } from "../components/nb";
import { Heart, Star, Users, MessageCircle, Crown, Mail, BadgeCheck, ArrowRight } from "lucide-react";

export default function Dashboard() {
  const { user, refresh } = useAuth();
  const [stats, setStats] = useState({ matches: 0, favorites: 0, threads: 0 });
  const [verifying, setVerifying] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [m, f, t] = await Promise.all([
          api.get("/matches"), api.get("/favorites"), api.get("/messages/threads"),
        ]);
        setStats({ matches: m.data.length, favorites: f.data.length, threads: t.data.length });
      } catch {}
    })();
  }, []);

  const verifyEmail = async () => {
    setVerifying(true);
    try {
      // mock: fetch token from /auth/me not available; use stored verify_token from register
      const token = localStorage.getItem("qrcupid_verify_token");
      if (!token) { toast.error("Verification token unavailable. Re-register to verify."); return; }
      await api.post("/auth/verify-email", { token });
      await refresh();
      localStorage.removeItem("qrcupid_verify_token");
      toast.success("Email verified! You earned a verified badge.");
    } catch (e) {
      toast.error("Verification failed.");
    } finally { setVerifying(false); }
  };

  const cards = [
    { label: "Matches", value: stats.matches, icon: Heart, to: "/matches", c: "#8B1E3F", txt: "text-white" },
    { label: "Favorites", value: stats.favorites, icon: Star, to: "/favorites", c: "#D4AF37", txt: "" },
    { label: "Conversations", value: stats.threads, icon: MessageCircle, to: "/messages", c: "#F3E8E6", txt: "" },
    { label: "Browse", value: "→", icon: Users, to: "/browse", c: "#D4AF37", txt: "" },
  ];

  return (
    <div className="min-h-screen grain">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <h1 className="font-head font-black text-4xl tracking-tighter">Hey, {user.name.split(" ")[0]} 👋</h1>
            <p className="text-gray-600 font-medium">Here's your dating HQ.</p>
          </div>
          <div className="flex items-center gap-2">
            {user.membership === "premium" ? (
              <span className="bg-[#D4AF37] nb-border nb-shadow-sm px-3 py-2 font-head font-bold text-sm uppercase flex items-center gap-1"><Crown className="w-4 h-4" /> Premium</span>
            ) : (
              <NbButton as={Link} to="/upgrade" variant="secondary" data-testid="dashboard-upgrade"><Crown className="w-4 h-4" /> Upgrade</NbButton>
            )}
          </div>
        </div>

        {!user.email_verified && (
          <div className="bg-[#D4AF37] nb-border nb-shadow p-4 mb-8 flex flex-wrap items-center justify-between gap-3" data-testid="verify-email-banner">
            <div className="flex items-center gap-2 font-medium"><Mail className="w-5 h-5" /> Verify your email to earn a <BadgeCheck className="w-4 h-4 inline" /> verified badge.</div>
            <NbButton variant="dark" onClick={verifyEmail} disabled={verifying} data-testid="verify-email-btn">{verifying ? "Verifying..." : "Verify Now"}</NbButton>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1">
            <QrCard user={user} />
            <NbButton as={Link} to="/profile" variant="outline" className="w-full mt-4" data-testid="dashboard-edit-profile">Edit Profile <ArrowRight className="w-4 h-4" /></NbButton>
          </div>

          <div className="lg:col-span-2">
            <div className="grid grid-cols-2 gap-4">
              {cards.map((c) => (
                <Link key={c.label} to={c.to} data-testid={`stat-${c.label.toLowerCase()}`} className={`nb-border nb-shadow nb-hover p-5 ${c.txt}`} style={{ background: c.c }}>
                  <c.icon className="w-7 h-7 mb-3" strokeWidth={2.5} />
                  <div className="font-head font-black text-4xl">{c.value}</div>
                  <div className="font-head font-bold uppercase text-sm tracking-wide">{c.label}</div>
                </Link>
              ))}
            </div>
            <div className="bg-white nb-border nb-shadow p-6 mt-4">
              <h3 className="font-head font-black text-xl mb-3">Profile completeness</h3>
              {(() => {
                const fields = ["bio", "age", "gender", "location", "occupation", "avatar"];
                const filled = fields.filter((f) => user[f]).length + (user.interests?.length ? 1 : 0);
                const pct = Math.round((filled / (fields.length + 1)) * 100);
                return (
                  <>
                    <div className="w-full bg-[#F3F3F1] nb-border h-6">
                      <div className="h-full bg-[#8B1E3F]" style={{ width: `${pct}%` }} />
                    </div>
                    <p className="mt-2 font-medium text-sm">{pct}% complete — {pct < 100 ? "finish your profile to get more matches." : "looking great!"}</p>
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
